import { useState } from "react";
import { PfeilRechts } from "./Symbole";
import { gleicherTag, heute, monatVerschoben, monatsname, monatsraster } from "../hilfen/datum";

/*
  Ein Monatsblatt.

  Montag bis Sonntag, immer sechs Zeilen — ein Blatt, das je nach Monat die
  Höhe wechselt, ließe beim Blättern alles darunter springen.

  ---------------------------------------------------------------
  Warum die Punkte keine Farbe tragen
  ---------------------------------------------------------------

  Die Oberfläche hat zwei farbige Zeichensysteme, die begründet sind: den
  Textmarker und die Abstimmungsampel. Ein drittes würde die
  Parteineutralität aufweichen. Sitzung und eigener Termin unterscheiden
  sich deshalb an der FORM des Punktes — gefüllt für das, was die Verwaltung
  einlädt, offen für das, was die Fraktion selbst verabredet hat.

  Höchstens drei Punkte je Tag, danach eine Zahl. Vier Punkte in einer Zelle
  von zwei Zentimetern kann niemand mehr zählen.

  ---------------------------------------------------------------
  Antippen
  ---------------------------------------------------------------

  Jeder Tag ist eine Antippfläche, keine Tabellenzelle mit einer Zahl darin.
  Auf dem iPad muss ein Tag mit dem Daumen zu treffen sein — das ist die
  Regel „große Antippflächen“ aus dem Steckbrief, und ein Kalender ist die
  Stelle, an der man sie am leichtesten bricht.
*/

const WOCHENKOEPFE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export default function Kalenderblatt({ eintraege, aufTag, gewaehlterTag }) {
  const [monat, setzeMonat] = useState(() => {
    const d = heute();
    d.setDate(1);
    return d;
  });

  const wochen = monatsraster(monat);
  const jetzt = heute();

  const anTag = (tag) => eintraege.filter((e) => gleicherTag(e.beginn, tag));

  return (
    <div className="kalender">
      <div className="kalender__kopf">
        <button
          className="kalender__blaettern"
          aria-label="Vorheriger Monat"
          onClick={() => setzeMonat(monatVerschoben(monat, -1))}
        >
          <span className="kalender__pfeil kalender__pfeil--zurueck">
            <PfeilRechts groesse={18} />
          </span>
        </button>

        <p className="kalender__monat" aria-live="polite">
          {monatsname(monat)}
        </p>

        <button
          className="kalender__blaettern"
          aria-label="Nächster Monat"
          onClick={() => setzeMonat(monatVerschoben(monat, 1))}
        >
          <PfeilRechts groesse={18} />
        </button>
      </div>

      <div className="kalender__wochenkopf" aria-hidden="true">
        {WOCHENKOEPFE.map((k) => (
          <span key={k}>{k}</span>
        ))}
      </div>

      <div className="kalender__raster">
        {wochen.map((woche, i) =>
          woche.map(({ datum, imMonat }) => {
            const drauf = anTag(datum);
            const istHeute = gleicherTag(datum, jetzt);
            const gewaehlt = gewaehlterTag && gleicherTag(datum, gewaehlterTag);

            return (
              <button
                key={`${i}-${datum.getTime()}`}
                className={[
                  "kalender__tag",
                  imMonat ? "" : "kalender__tag--fremd",
                  istHeute ? "kalender__tag--heute" : "",
                  gewaehlt ? "kalender__tag--gewaehlt" : "",
                  drauf.length ? "kalender__tag--belegt" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => aufTag(drauf.length ? datum : null)}
                aria-label={beschriftung(datum, drauf)}
                aria-pressed={Boolean(gewaehlt)}
              >
                <span className="kalender__zahl">{datum.getDate()}</span>
                <Punkte eintraege={drauf} />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/*
  Höchstens drei Punkte, danach die Zahl. Die Punkte tragen zusätzlich einen
  Titel, damit auch am Rechner erkennbar ist, was dahintersteckt — auf dem
  Tablet übernimmt das Antippen des Tages.
*/
function Punkte({ eintraege }) {
  if (eintraege.length === 0) return <span className="kalender__punkte" />;

  if (eintraege.length > 3) {
    return <span className="kalender__mehr">{eintraege.length}</span>;
  }

  return (
    <span className="kalender__punkte">
      {eintraege.map((e) => (
        <span
          key={e.id}
          className={`kalender__punkt ${
            e.art === "termin" ? "kalender__punkt--eigen" : ""
          }`}
          title={e.name}
        />
      ))}
    </span>
  );
}

/*
  Für Sprachausgabe und Tastatur: Ein Knopf, der nur „14“ heißt, sagt nichts.
*/
function beschriftung(datum, eintraege) {
  const tag = datum.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  if (eintraege.length === 0) return `${tag}, nichts eingetragen`;
  return `${tag}, ${eintraege.map((e) => e.name).join(", ")}`;
}
